import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { tickerDetailService } from '../../services/tickerDetailService';
import { SectionSkeleton, SectionError } from './CompanyProfile';
import { FinancialsChart } from './FinancialsChart';
import type { IncomeStatementRow, BalanceSheetRow, CashFlowRow } from '../../types/tickerDetail';

interface Props { symbol: string; }

type Period = 'annual' | 'quarter';
type SubTab = 'income' | 'balance' | 'cash_flow';

function fmt(n: number | null | undefined): string {
  if (n == null || isNaN(n)) return '\u2014';
  if (Math.abs(n) >= 1e9) return `${(n / 1e9).toFixed(1)}B`;
  if (Math.abs(n) >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
  return n.toLocaleString();
}

export function Financials({ symbol }: Props): React.ReactElement {
  const [period, setPeriod] = useState<Period>('annual');
  const [subTab, setSubTab] = useState<SubTab>('income');

  const { data, isLoading, isError } = useQuery({
    queryKey: ['tickerFinancials', symbol, period],
    queryFn: () => tickerDetailService.getFinancials(symbol, period),
    staleTime: 4 * 60 * 60 * 1000,
    enabled: !!symbol,
  });

  const financials = data?.data;

  if (isLoading) return <SectionSkeleton />;
  if (isError || !financials) return <SectionError message="Failed to load financials" />;

  const rows =
    subTab === 'income'
      ? financials.income
      : subTab === 'balance'
        ? financials.balance
        : financials.cash_flow;

  return (
    <section className="p-4 border-b border-dark-border">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <h2 className="text-sm font-semibold text-gray-300 uppercase tracking-wider">
          Financials
        </h2>
        <div className="flex gap-2">
          {/* Period toggle */}
          <div className="flex text-xs rounded border border-dark-border overflow-hidden">
            {(['annual', 'quarter'] as Period[]).map((p) => (
              <button
                key={p}
                onClick={() => setPeriod(p)}
                className={`px-3 py-1 transition-colors capitalize ${
                  period === p
                    ? 'bg-accent-yellow text-hot-tag-text font-semibold'
                    : 'bg-dark-bg text-gray-400 hover:text-gray-200'
                }`}
              >
                {p === 'annual' ? 'Annual' : 'Quarterly'}
              </button>
            ))}
          </div>
          {/* Sub-tab */}
          <div className="flex text-xs rounded border border-dark-border overflow-hidden">
            {([['income', 'Income'], ['balance', 'Balance Sheet'], ['cash_flow', 'Cash Flow']] as [SubTab, string][]).map(
              ([key, label]) => (
                <button
                  key={key}
                  onClick={() => setSubTab(key)}
                  className={`px-3 py-1 transition-colors ${
                    subTab === key
                      ? 'bg-accent-yellow text-hot-tag-text font-semibold'
                      : 'bg-dark-bg text-gray-400 hover:text-gray-200'
                  }`}
                >
                  {label}
                </button>
              )
            )}
          </div>
        </div>
      </div>

      {/* Chart first: the shape of the business reads faster than the rows,
          which stay one click away for the exact figures. */}
      <FinancialsChart rows={rows} view={subTab} period={period} />

      <details className="mt-3 group">
        <summary className="cursor-pointer text-xs text-gray-400 hover:text-gray-200 select-none">
          <span className="group-open:hidden">Show figures</span>
          <span className="hidden group-open:inline">Hide figures</span>
        </summary>
        <div className="overflow-x-auto mt-2">
          {subTab === 'income' && (
            <FinancialsTable
              rows={financials.income}
              columns={[
                { key: 'revenue', label: 'Revenue' },
                { key: 'grossProfit', label: 'Gross Profit' },
                { key: 'operatingIncome', label: 'Operating Income' },
                { key: 'netIncome', label: 'Net Income' },
                { key: 'ebitda', label: 'EBITDA' },
                { key: 'eps', label: 'EPS' },
              ]}
            />
          )}
          {subTab === 'balance' && (
            <FinancialsTable
              rows={financials.balance}
              columns={[
                { key: 'totalAssets', label: 'Total Assets' },
                { key: 'totalLiabilities', label: 'Total Liabilities' },
                { key: 'totalStockholdersEquity', label: 'Equity' },
                { key: 'cashAndCashEquivalents', label: 'Cash' },
                { key: 'totalDebt', label: 'Total Debt' },
              ]}
            />
          )}
          {subTab === 'cash_flow' && (
            <FinancialsTable
              rows={financials.cash_flow}
              columns={[
                { key: 'operatingCashFlow', label: 'Operating CF' },
                { key: 'capitalExpenditure', label: 'CapEx' },
                { key: 'freeCashFlow', label: 'Free CF' },
                { key: 'dividendsPaid', label: 'Dividends Paid' },
              ]}
            />
          )}
        </div>
      </details>
    </section>
  );
}

type FinancialRow = IncomeStatementRow | BalanceSheetRow | CashFlowRow;

function FinancialsTable<T extends FinancialRow>({
  rows,
  columns,
}: {
  rows: T[];
  columns: { key: keyof Omit<T, 'date' | 'period'>; label: string }[];
}): React.ReactElement {
  if (!rows.length) return <p className="text-gray-400 text-sm">No data</p>;
  return (
    <table className="w-full text-xs text-left border-collapse">
      <thead>
        <tr className="border-b border-dark-border">
          <th className="py-1.5 pr-4 text-gray-400 font-medium">Period</th>
          {columns.map((c) => (
            <th key={String(c.key)} className="py-1.5 pr-4 text-gray-400 font-medium text-right">
              {c.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, i) => (
          <tr key={i} className="border-b border-dark-border/50 hover:bg-dark-surface/50">
            <td className="py-1.5 pr-4 text-white font-numeric">{row.date}</td>
            {columns.map((c) => (
              <td key={String(c.key)} className="py-1.5 pr-4 text-gray-200 text-right font-numeric">
                {fmt(row[c.key] as number)}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}
